import { auth, firestore } from "firebase/app";
import {
  getClientRef,
  getBranchesOfClient,
  hasClientEnabledStrictBranchFilters,
  getClientRefById,
  isSimulatingErp,
  getBranchesOfMemberClient,
  getIgnoreIresAdminOrIresClientCheck,
  getClientsAdministratorId,
} from "@/data/repository/ClientRepository";
import { db, functions } from "@/main";
import {
  setString,
  getString,
  setBoolean,
  getBoolean,
} from "@/utils/SecureLocalStorage";
import { clearViewAsSelection } from "@/utils/ViewAsSession";
import { getEffectiveUserLazily } from "@/utils/EffectiveUserLazy";
import { UserModule, IRES_ADMIN_ACCOUNT_ID } from "@/store/modules/UserModule";
import { Licence } from "@/data/models/Licence";

import axios from "axios";
import User, {
  Role,
  MASTER_ID,
  isIresAdminAccount,
  WEB_ADMIN_ID,
  getLatestLicences,
  pickUserPreferringActive,
  UserOperationSource as OperationSource,
} from "@/data/models/User";
import { generateRandomUuid, filterOutUndefined } from "@/utils/Utils";
import { LoginResult, loginResultForRefusal } from "@/components/login/LoginTypes";
import { toSignInRefusalReason } from "@/utils/SignInRefusal";
import { BranchFilter } from "@/data/models/BranchFilter";
import { hashPassword } from "@/utils/PasswordUtils";
import { SharedEmailAddressInfo } from "../models/SharedEmailAddressInfo";
import { grantAllLicencesAccess, skipSettingSessionTokenAccess } from "@/permissions/UserPermissions";
import Client from "../models/Client";
import { Branch } from "../models/Branch";
import { MiraSavedComparison } from "@/data/models/MiraSavedComparison";

const SIGN_IN_SECRET = "yhzQW64w3fgCXaoF26YdDYAoAYQkcosmWxOLnvP3";
const CURRENT_USER = "CURRENT_USER";
const SESSION_ENDED = "SESSION_ENDED";

const HEADERS = {
  "Content-Type": "application/json",
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET,POST,DELETE,HEAD,PUT,OPTIONS",
  "Access-Control-Allow-Headers":
    "Origin, X-Requested-With, Content-Type, Accept",
};

const BASE_URL = "https://europe-west1-ires-cloud.cloudfunctions.net/"; 

function isNetworkBlockedError(error: unknown) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const firebaseCode = (error as any)?.code;
  const message = (error as Error)?.message?.toLowerCase?.();

  return (
    firebaseCode === "unavailable" ||
    firebaseCode === "auth/network-request-failed" ||
    firebaseCode === "deadline-exceeded" ||
    (message?.includes("could not reach cloud firestore backend") ?? false) ||
    (message?.includes("backend didn't respond within 10 seconds") ?? false)
  );
}

export interface IsEmailAlreadyUsedResult {
  isEmailAlreadyUsed: boolean;
  sharedEmailAddressId?: string;
}

export interface SignInWithSharedEmailAddressResult {
  wasSuccessful: boolean;
  token?: string;
  /** Why IRES Cloud refused it (#2610066): `CLIENT_BLOCKED` or `USER_BLOCKED`. */
  reason?: string;
}

export function getRecordInfo() {
  const activeUser = UserModule.activeUser;

  const now = firestore.Timestamp.fromDate(new Date());

  if (activeUser) {
    return {
      recordedAt: now,
      recordedByUserId: isSimulatingErp() 
        ? activeUser.nickname 
          ? "ERP/"+activeUser.nickname 
          : "ERP/"+activeUser.id 
        : activeUser.id,
    };
  } else {
    return {
      recordedAt: now,
      recordedByUserId: MASTER_ID,
    };
  }
}

function getUsersRef() {
  return getClientRef() + "/users";
}

function getUsersRefByClientId(clientId: string) {
  return getClientRefById(clientId) + "/users";
}

export function getUserRef(userId: string) {
  return db.collection(getUsersRef()).doc(userId);
}

export function getUserRefByClientId(clientId: string, userId: string) {
  return db.collection(getUsersRefByClientId(clientId)).doc(userId);
}

export function storeSessionEnded() {
  setBoolean(SESSION_ENDED, true);
}

export function clearSessionEnded() {
  setBoolean(SESSION_ENDED, null);
}

export function didSessionEnd() {
  return getBoolean(SESSION_ENDED) === true;
}

export async function loadUsers() {
  const result = await db.collection(getUsersRef()).get();
  return result.docs.map((doc) => doc.data() as User);
}

export enum UserUpdateReasonForFailure {
  NICKNAME_ALREADY_USED = "NICKNAME_ALREADY_USED",
  STAFF_NUMBER_ALREADY_USED = "STAFF_NUMBER_ALREADY_USED",
  LICENCE_LIMIT_EXCEEDED = "LICENCE_LIMIT_EXCEEDED",
  EMAIL_ALREADY_USED = "EMAIL_ALREADY_USED",
  INVALID_FIELD = "INVALID_FIELD",
}

export interface StoreUserUpdateResult {
  wasSuccessful: boolean;
  reasonForFailure?: UserUpdateReasonForFailure;
  exceededLicences?: Licence[];
  field?: string;
}

export function getActiveUser() {
  const userJson = getString(CURRENT_USER);

  if (userJson) {
    return JSON.parse(userJson) as User;
  } else {
    return null;
  }
}

/**
 * Reactive variant of {@link getActiveUser} for use in Vue computed getters /
 * templates that DISPLAY current-user fields (role, permissions, nickname, …).
 *
 * `getActiveUser()` reads `SecureLocalStorage`, which Vue cannot track, so a
 * computed built on it never re-evaluates when the current user's own data
 * changes in-session (e.g. a role edit picked up by `observeUsers`). This
 * resolves the user id from localStorage (reliable, including right after a
 * reload before the store is populated), then returns the reactive
 * `UserModule` copy so the render updates without a page refresh, falling back
 * to the localStorage snapshot until the store has the user. Preserves
 * `getActiveUser`'s null-when-unset semantics (never substitutes `master`).
 */
export function getReactiveActiveUser(): User | null {
  const storedUser = getActiveUser();

  if (storedUser === null) {
    return UserModule.activeUser;
  }

  return UserModule.user(storedUser.id) ?? storedUser;
}

export async function storeUserUpdate(
  userId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  update: any,
  temporaryPassword?: string,
  shouldSkipSharedUserOfGroupUpdate?: boolean
): Promise<StoreUserUpdateResult> {
  /**
   * Keep the current session token when the active user updates their own profile.
   * Otherwise the portalSessionToken gets overwritten/removed by the backend update,
   * triggering the observeUsers sign-out logic on the next snapshot.
   */
  const activeUser = getActiveUser();
  const activePortalSessionToken = activeUser?.licenceInfo?.portalSessionToken;

  if (userId === activeUser?.id && activePortalSessionToken) {
    if (!update.licenceInfo) {
      update.licenceInfo = { portalSessionToken: activePortalSessionToken };
    } else if (update.licenceInfo.portalSessionToken === undefined) {
      update.licenceInfo = {
        ...update.licenceInfo,
        portalSessionToken: activePortalSessionToken,
      };
    }
  }

  if (temporaryPassword) {
    const { salt, passwordHashHex } = hashPassword(temporaryPassword);
    
    update.passwordHashHex = passwordHashHex;
    update.salt = salt;
    update.hasTemporaryPassword = true;
  }

  update.id = userId;

  const createOrUpdateUser = functions.httpsCallable("createOrUpdateUser");

  return (
    await createOrUpdateUser({
      user: JSON.stringify(update),
      changeMadeBy: UserModule.activeUserId,
      shouldSkipSharedUserOfGroupUpdate: shouldSkipSharedUserOfGroupUpdate ?? false
    })
  ).data as StoreUserUpdateResult;
}

/**
 * Persists the active user's dark-mode preference to their user document so it
 * follows them across devices/browsers. Best-effort: a direct merge write touches
 * only `darkMode` (so it never disturbs the portal session token), and the value is
 * also cached in localStorage, so failures here are non-critical and swallowed.
 */
export async function persistActiveUserDarkMode(isDark: boolean): Promise<void> {
  const user = getActiveUser();

  if (!user?.id || user.id === "Master") {
    return;
  }

  try {
    await getUserRef(user.id).set({ darkMode: isDark }, { merge: true });
  } catch (e) {
    // Non-critical: the preference remains cached in localStorage.
  }
}

/**
 * Saves the active user's choice of figures for one Mira landing card (#2610102), so it follows them
 * across devices and browsers. A direct merge write of that one key only, like
 * {@link persistActiveUserDarkMode}, so it never disturbs the portal session token; the users snapshot
 * then re-stores the active user with it. `null` goes back to the default (the screen's first figures).
 *
 * Unlike the dark-mode write this one throws: the user pressed SICHERN, so a failure must be shown.
 */
export async function persistActiveUserMiraLandingFigures(section: string, metricIds: string[] | null): Promise<void> {
  const user = getActiveUser();

  if (!user?.id || user.id === "Master") {
    throw new Error("No user record to store the Mira figures on");
  }

  await getUserRef(user.id).set(
    { miraLandingFigures: { [section]: metricIds ?? firestore.FieldValue.delete() } },
    { merge: true }
  );
}

/**
 * Saves the active user's saved Mira comparisons for one client (#2610137 tasks 22 / 24): the whole list
 * of that client, so a deletion and a reorder are one write. A direct merge write of that one key, like
 * {@link persistActiveUserMiraLandingFigures}; an empty list removes the client's entry. Throws, so the
 * dialog can show a failure.
 */
export async function persistActiveUserMiraComparisons(clientId: string, comparisons: MiraSavedComparison[]): Promise<void> {
  const user = getActiveUser();

  if (!user?.id || user.id === "Master") {
    throw new Error("No user record to store the Mira comparisons on");
  }

  await getUserRef(user.id).set(
    { miraComparisons: { [clientId]: comparisons.length > 0 ? comparisons : firestore.FieldValue.delete() } },
    { merge: true }
  );
}

export async function setNewPassword(
  userId: string, 
  password: string,
  user: User
) {
  const userRef = getUserRef(userId);
  const { salt, passwordHashHex } = hashPassword(password);

  try {
    await db.runTransaction(async (transaction) => {
      transaction.set(
        userRef, 
        {
          passwordHashHex,
          salt,
          hasTemporaryPassword: false,
        },
        { merge: true }
      );

      const administratorClientId = getClientsAdministratorId();
      if (user.sharedUserOfGroup && administratorClientId) {
        const updateSharedUserOfGroupPassword = functions.httpsCallable('updateSharedUserOfGroupPassword');

        const payload = filterOutUndefined({
          userId,
          passwordHashHex,
          salt,
          administratorClientId,
        });

        await updateSharedUserOfGroupPassword(payload);
      }
    });
  } catch (e) {
    // Intentionally empty
  }

  return;
}

export async function loadUser(userId: string) {
  const result = await getUserRef(userId).get();
  return result.data() as User;
}

export interface AssignLicencesResult {
  wasSuccessful: boolean;
  noUserFound: boolean;
  missingLicences: Array<string>;
}

export async function assignLicencesToUser(
  userId: string,
  licences: Array<Licence>
) {
  const assignLicences = functions.httpsCallable("assignLicences");

  return (
    await assignLicences({
      userId: userId,
      licences: licences,
    })
  ).data as AssignLicencesResult;
}

export interface CheckUserPasswordResult {
  isCorrectPassword: boolean;
}

export async function checkUserPassword(userId: string, password: string) {
  const assignLicences = functions.httpsCallable("checkUserPassword");

  return (
    await assignLicences({
      userId: userId,
      password: password,
    })
  ).data as CheckUserPasswordResult;
}

export async function setPortalSessionToken(userId: string, sessionToken: string) {
  await db
    .collection(getUsersRef())
    .doc(userId)
    .set(
      {
        licenceInfo: {
          portalSessionToken: sessionToken,
        },
      },
      { merge: true }
    );
}

/**
 * Clears the portalSessionToken from a user's licenceInfo in Firestore.
 * Must be called on logout to invalidate the session token.
 */
export async function clearPortalSessionToken(userId: string) {
  await db
    .collection(getUsersRef())
    .doc(userId)
    .update({
      "licenceInfo.portalSessionToken": firestore.FieldValue.delete(),
    });
}

/**
 * Whether the user whose view the app shows is the IRES admin account: the chosen user while
 * "Sicht auf" is active (ticket 2610052), so an IRES admin viewing a client user loses the IRES-only
 * client features that user does not have. Its action callers (PDF creation, copying devices) are
 * refused while viewing anyway.
 */
export function isActiveUserIresAdminAccount(skipIgnoreIresAdminOrIresClientCheck?: boolean): boolean {
  const activeUser = getEffectiveUserLazily();

  if (!activeUser) {
    return false;
  }

  if (!skipIgnoreIresAdminOrIresClientCheck && getIgnoreIresAdminOrIresClientCheck()) {
    return false;
  }

  return isIresAdminAccount(activeUser);
}

export function getActiveUserId() {
  const userJson = getString(CURRENT_USER);

  if (userJson) {
    return JSON.parse(userJson)?.id;
  } else {
    return "Master";
  }
}

export function storeActiveUser(user: User, password: string | null = null) {
  // A different signed-in user (a login, a shared user switch) never inherits "Sicht auf"
  // (ticket 2610050). The users snapshot re-stores the same user and keeps it.
  if (getActiveUser()?.id !== user.id) {
    clearViewAsSelection();
  }

  UserModule.setActiveUserId(user.id);
  
  if (password) {
    UserModule.setSharedUserPassword(password);
  }

  const userToStore = {
    ...user,
  };

  userToStore.passwordHash = undefined;
  userToStore.passwordHashBase64 = undefined;
  userToStore.passwordHashHex = undefined;
  userToStore.hasTemporaryPassword = undefined;
  userToStore.salt = undefined;

  setString(CURRENT_USER, JSON.stringify(filterOutUndefined(userToStore)));
}

export async function setActiveUser(userId: string, password: string | null = null) {
  let user: User;

  try {
    user = await loadUser(userId);
  } catch (error) {
    if (!navigator.onLine) {
      return LoginResult.NO_INTERNET_CONNECTION;
    }

    if (isNetworkBlockedError(error)) {
      return LoginResult.NETWORK_BLOCKED;
    }

    throw error;
  }

  if (user.blocked) {
    await auth().signOut();
    return LoginResult.USER_BLOCKED;
  }

  const sessionToken = generateRandomUuid();

  if (user.licenceInfo) {
    user.licenceInfo.portalSessionToken = sessionToken;
  } else {
    user.licenceInfo = {
      portalSessionToken: sessionToken,
      licences: [],
    };
  }

  if (password) {
    storeActiveUser(user, password);
  }
  else {
    storeActiveUser(user);
  }

  if (!skipSettingSessionTokenAccess(user)) {
    try {
      await setPortalSessionToken(userId, sessionToken);
    } catch (error) {
      if (!navigator.onLine) {
        return LoginResult.NO_INTERNET_CONNECTION;
      }

      if (isNetworkBlockedError(error)) {
        return LoginResult.NETWORK_BLOCKED;
      }

      throw error;
    }
  }

  return LoginResult.SUCCESS;
}

/**
 * Observes the signed-in client's users, keeps `UserModule` current and reports changes to the
 * active user.
 *
 * @param onActiveUserChanged - called when the active user's own record changes.
 * @param onAccessRefused - called when the rules stop letting this session read the users. On the IRES
 *   Admin Client that means the active user was blocked or deleted (#2610124): IRES Cloud's rules
 *   then refuse the session everything, so the change itself never arrives here.
 */
export function observeUsers(
  onActiveUserChanged: (before: User, after: User) => void,
  onAccessRefused?: () => void
) {
  return db.collection(getUsersRef()).onSnapshot((snapshot) => {
    const users = snapshot.docs
      .map((doc) => doc.data() as User)
      .filter((user) => user.id);
    const activeUser = getActiveUser();

    if (activeUser) {
      const updatedActiveUser = users.find((user) => user.id == activeUser.id);

      if (updatedActiveUser && activeUser != updatedActiveUser) {
        onActiveUserChanged(activeUser, updatedActiveUser);
        storeActiveUser(updatedActiveUser);
      }
    }

    UserModule.setUsers(users);
  }, (error) => {
    if (error.code === "permission-denied") {
      onAccessRefused?.();
    }
  });
}

export async function isEmailAlreadyUsed(email: string, userId: string) {
  const isEmailAlreadyUsed = functions.httpsCallable("isEmailAlreadyUsed");

  return (
    await isEmailAlreadyUsed({
      userId: userId,
      emailAddress: email,
    })
  ).data as IsEmailAlreadyUsedResult;
}

export async function signInWithUserOfSharedEmailAddress(
  sharedEmailAddressId: string,
  userId: string,
  clientId: string,
  calledViaHeader = false,
) {
  const signInWithUserOfSharedEmailAddress = functions.httpsCallable(
    "signInWithUserOfSharedEmailAddress"
  );

  return (
    await signInWithUserOfSharedEmailAddress({
      userId: userId,
      sharedEmailAddressId: sharedEmailAddressId,
      clientId: clientId,
      calledViaHeader: calledViaHeader,
    })
  ).data as SignInWithSharedEmailAddressResult;
}

export async function signInWithSharedUserOfGroup(
  emailAddress: string,
  targetClientId: string,
  ignoreSourceClientIdCheck = false,
) {
  const signInWithSharedUserOfGroup = functions.httpsCallable(
    "signInWithSharedUserOfGroup"
  );

  return (
    await signInWithSharedUserOfGroup({
      emailAddress: emailAddress,
      targetClientId: targetClientId,
      ignoreSourceClientIdCheck: ignoreSourceClientIdCheck
    })
  ).data as SignInWithSharedEmailAddressResult;
}

export async function signInWithUserEmail(email: string, password: string) {
  const payload = JSON.stringify({
    data: {
      password: password,
      emailAddress: email,
      secret: SIGN_IN_SECRET,
      source: OperationSource.PORTAL
    },
  });
  
  try {
    const response = await axios.post(
      `${BASE_URL}signInWithUserEmail`,
      payload,
      { headers: HEADERS }
    );

    const {
      wasSuccessful,
      token,
      userId,
      sharedEmailAddressId,
      reason,
    } = response?.data?.data;

    if (wasSuccessful && token) {
      await auth().signInWithCustomToken(token);

      if (sharedEmailAddressId) {
        return {
          sharedEmailAddressId: sharedEmailAddressId,
          sharedUserPassword: password,
          result: LoginResult.SHARED_EMAIL_ADDRESS,
        };
      } else {
        const setUserResult = await setActiveUser(userId);

        return {
          result: setUserResult,
        };
      }
    } else if (!wasSuccessful) {
      // #2610066: a blocked or deleted client, or a blocked user, comes with its reason.
      const refusal = toSignInRefusalReason(reason);

      return {
        result: refusal ? loginResultForRefusal(refusal) : LoginResult.WRONG_PASSWORD,
      };
    }

    return {
      result: LoginResult.NO_MATCHING_CLIENT_OR_USER,
    };
  } catch (error) {
    const isOffline = !navigator.onLine;
    const isNetworkBlocked =
      (axios.isAxiosError(error) && !error.response) ||
      isNetworkBlockedError(error);

    if (isOffline) {
      return {
        result: LoginResult.NO_INTERNET_CONNECTION,
      };
    }

    if (isNetworkBlocked) {
      return {
        result: LoginResult.NETWORK_BLOCKED,
      };
    }

    return {
      result: LoginResult.NO_MATCHING_CLIENT_OR_USER,
    };
  }
}

/**
 * Requests a password reset for the given email address.
 * If the user exists, the system will generate a reset token, send a reset email,
 * and respond with a success status. If the user does not exist, it will still return success,
 * avoiding user enumeration.
 *
 * @async
 * @function requestResetPassword
 * @param {string} email - The email address of the user requesting a password reset.
 * @param {boolean} isDevelopmentWebsite - A flag indicating if the environment is a development environment.
 * @returns {Promise<{ wasSuccessful: boolean; isSharedUserEmailAddress: boolean }>}
 * - An object containing:
 *   - `wasSuccessful`: Indicates whether the password reset request was processed successfully.
 *   - `isSharedUserEmailAddress`: Indicates if the provided email is associated with a shared email user.
 * @throws {Error} - Throws an error if the HTTP request fails.
 */
export async function requestResetPassword(
  email: string,
  isDevelopmentWebsite: boolean,
): Promise<{ wasSuccessful: boolean; isSharedUserEmailAddress: boolean}> {
  const baseUrl = isDevelopmentWebsite
    ? "https://development.hydry.de/"
    : "https://portal.hydry.de/";

  const payload = JSON.stringify({
    data: {
      emailAddress: email,
      baseUrl: baseUrl,
      secret: SIGN_IN_SECRET,
    },
  });

  const response = await axios.post(
    `${BASE_URL}requestPasswordReset`,
    payload,
    { headers: HEADERS }
  );

  const {
    wasSuccessful,
    isSharedUserEmailAddress
  } = response?.data?.data;

  return {
    wasSuccessful: (wasSuccessful as boolean) ?? false,
    isSharedUserEmailAddress: (isSharedUserEmailAddress as boolean) ?? false
  };
}


/**
 * Validates a given password reset token for a specified user and client.
 * This checks whether the token is valid and not expired.
 *
 * @async
 * @function validateResetToken
 * @param {string} clientId - The unique identifier of the client.
 * @param {string} userId - The unique identifier of the user whose token is being validated.
 * @param {string} token - The password reset token to validate.
 * @returns {Promise<boolean>} - Returns `true` if the token is valid and not expired, otherwise `false`.
 * @throws {Error} - Throws an error if the HTTP request fails.
 */
export async function validateResetToken(
  clientId: string,
  userId: string,
  token: string,
): Promise<boolean> {
  const payload = JSON.stringify({
    data: {
      clientId: clientId,
      userId: userId,
      token: token,
      secret: SIGN_IN_SECRET,
    },
  });

  const response = await axios.post(
    `${BASE_URL}verifyPasswordResetToken`,
    payload,
    { headers: HEADERS }
  );

  const {
    wasSuccessful
  } = response?.data?.data;

  return (wasSuccessful as boolean) ?? false;
}

/**
 * Resets a user's password using a valid reset token.
 * If the token is valid and not expired, the user's password is updated.
 *
 * @async
 * @function resetPassword
 * @param {string} clientId - The ID of the client associated with the user.
 * @param {string} userId - The ID of the user whose password is being reset.
 * @param {string} newPassword - The new password to set for the user.
 * @param {string} token - The password reset token provided to the user.
 * @returns {Promise<boolean>} - Returns `true` if the password was successfully reset, otherwise `false`.
 * @throws {Error} - Throws an error if the HTTP request fails.
 */
export async function resetPassword(
  clientId: string,
  userId: string,
  newPassword: string,
  token: string,
): Promise<boolean> {
  const payload = JSON.stringify({
    data: {
      clientId: clientId,
      userId: userId,
      newPassword: newPassword,
      token: token,
      secret: SIGN_IN_SECRET,
    },
  });

  const response = await axios.post(
    `${BASE_URL}resetPassword`,
    payload,
    { headers: HEADERS }
  );

  const {
    wasSuccessful
  } = response?.data?.data;

  return (wasSuccessful as boolean) ?? false;
}

export function findUser(userId?: string | null) {
  const users = UserModule.users;

  if (!userId) {
    return null;
  }

  // Each stage prefers an active user, because a single identifier (a shared staffNumber above all)
  // can match several users, deleted ones among them.
  return (
    pickUserPreferringActive(users, (user) => user.id === userId) ??
    pickUserPreferringActive(users, (user) => user.staffNumber === userId) ??
    pickUserPreferringActive(users, (user) => user.nickname === userId) ??
    pickUserPreferringActive(
      users,
      (user) => `${user.firstName} ${user.lastName}` === userId
    ) ??
    null
  );
}

export function findNickname(userId?: string | null) {
  if (!userId) {
    return undefined;
  }

  const user = findUser(isSimulatingErp() && userId.includes("-") ? userId.split("-")[1] : userId);
  const isValidSimulatingErp = userId.includes("-");

  return user ? (isSimulatingErp() && isValidSimulatingErp ? "ERP/"+user.nickname : user.nickname) : userId;
}

export function getActiveUserRole() {
  const activeUser = getActiveUser();
  return activeUser ? activeUser.role : Role.ADMIN;
}

export function canActiveUserControlComBoxViaIot() {
  const activeUserRole = getActiveUserRole();

  return (
    activeUserRole === Role.ADMIN || activeUserRole === Role.PROJECT_MANAGER
  );
}

export function filterUsersForSelection(users: User[]) {
  return users
    .filter((user: User) => !user.deleted && user.id !== IRES_ADMIN_ACCOUNT_ID)
    .sort((u1, u2) => u1.nickname.localeCompare(u2.nickname));
}

export function hasLicencesV3(user: User | null | undefined) {
  return user?.licenceInfo?.licencesV3 ? true : false;
}

export function getLicencesOfUser(user: User | null | undefined) {
  if (user) {
    if (grantAllLicencesAccess(user)) {
      if (hasLicencesV3(user)) {
        return [
          Licence.BASIC_LICENCE,
          Licence.DRYING_AND_REPAIR_LICENCE,
          Licence.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION_LICENCE,
        ];
      }
      else {
        return [
          Licence.BASIC_LICENCE,
          Licence.DRYING_PROJECT_LICENCE,
          Licence.LEAK_DETECTION_PROJECT_LICENCE,
        ];
      }
      
    } else if (user.id === WEB_ADMIN_ID) {
      return [Licence.BASIC_LICENCE];
    } else if (user.id === MASTER_ID) {
      if (hasLicencesV3(user)) {
        return [
          Licence.BASIC_LICENCE,
          Licence.DRYING_AND_REPAIR_LICENCE,
          Licence.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION_LICENCE,
        ];
      }
      else {
        return [
          Licence.BASIC_LICENCE,
          Licence.DRYING_PROJECT_LICENCE,
          Licence.LEAK_DETECTION_PROJECT_LICENCE,
        ];
      }
    }

    return getLatestLicences(user.licenceInfo) ?? [];
  } else {
    if (hasLicencesV3(user)) {
      return [
        Licence.BASIC_LICENCE,
        Licence.DRYING_AND_REPAIR_LICENCE,
        Licence.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION_LICENCE,
      ];
    }
    else {
      return [
        Licence.BASIC_LICENCE,
        Licence.DRYING_PROJECT_LICENCE,
        Licence.LEAK_DETECTION_PROJECT_LICENCE,
      ];
    }
  }
}

export function hasUserDryingProjectLicence(user: User | null | undefined) {
  const licenses = getLicencesOfUser(user);
  const hasValidLicences = 
    hasLicencesV3(user) ? 
    licenses != undefined && licenses.indexOf(Licence.DRYING_AND_REPAIR_LICENCE) >= 0 : 
    licenses != undefined && licenses.indexOf(Licence.DRYING_PROJECT_LICENCE) >= 0;
  return (
    hasValidLicences
  );
}

export function hasUserLeakDetectionProjectLicence(
  user: User | null | undefined
) {
  const licenses = getLicencesOfUser(user);
  const hasValidLicences = 
    hasLicencesV3(user) ? 
    licenses != undefined && licenses.indexOf(Licence.DAMAGE_ASSESSMENT_AND_LEAK_DETECTION_LICENCE) >= 0 : 
    licenses != undefined && licenses.indexOf(Licence.LEAK_DETECTION_PROJECT_LICENCE) >= 0;

  return (
    hasValidLicences
  );
}

export function getBranchesVisibleToUser(user: User | null | undefined) {
  const branchesOfUser = user?.branches ?? [];
  const branchesOfClient = getBranchesOfClient();

  if (branchesOfUser.length === 0) {
    return branchesOfClient;
  }

  return branchesOfClient.filter(
    (branch) => branchesOfUser.indexOf(branch.id) >= 0
  );
}

/**
 * The branches the user whose view the app shows may see: the chosen user's while "Sicht auf" is
 * active (ticket 2610052), so lists default to their branches. The name predates "Sicht auf".
 */
export function getBranchesVisibleToActiveUser() {
  const activeUser = getEffectiveUserLazily();

  return getBranchesVisibleToUser(activeUser);
}

export function getBranchFilters(branches: Branch[]) {
  const strictBranchFilters = hasClientEnabledStrictBranchFilters();
  const addAllBranchesFilter = branches.length > 1;

  const result = addAllBranchesFilter
    ? [new BranchFilter(strictBranchFilters, ...branches)]
    : [];

  branches.forEach((branch) =>
    result.push(new BranchFilter(strictBranchFilters, branch))
  );

  return result;
}

export function getBranchFiltersOfUser(user: User | null | undefined) {
  const branches = getBranchesVisibleToUser(user);

  if (branches.length < 1) {
    return [];
  }

  return getBranchFilters(branches);
}

export function getBranchFiltersOfMemberClient(memberClient: Client) {
  const branches = getBranchesOfMemberClient(memberClient);

  if (branches.length < 1) {
    return [];
  }

  return getBranchFilters(branches);
}

/** As {@link getBranchesVisibleToActiveUser}: the chosen user's while "Sicht auf" is active. */
export function getBranchFiltersOfActiveUser() {
  return getBranchFiltersOfUser(getEffectiveUserLazily());
}

export function getDefaultBranchFilterOfUser(): BranchFilter {
  const strictBranchFilters = hasClientEnabledStrictBranchFilters();
  const branches = getBranchesVisibleToActiveUser();

  return (
    getBranchFiltersOfActiveUser()[0] ??
    new BranchFilter(strictBranchFilters, ...branches)
  );
}

/**
 * Blocks or unblocks all users sharing the same email address.
 * Routes through a cloud function for proper audit trail and cross-client writes.
 *
 * @param sharedEmailAddressInfo - The shared email address record containing all linked users.
 * @param blockUser - Whether to block (true) or unblock (false) the users.
 */
export async function blockSharedUsers(
  sharedEmailAddressInfo: SharedEmailAddressInfo, 
  blockUser: boolean,
) {
  const blockSharedEmailUsers = functions.httpsCallable("blockSharedEmailUsers");

  await blockSharedEmailUsers({
    sharedEmailAddressId: sharedEmailAddressInfo.id,
    blocked: blockUser,
  });
}

/**
 * Sets the password for all users sharing the same email address.
 * Routes through a cloud function for proper audit trail and cross-client writes.
 *
 * @param sharedEmailAddressInfo - The shared email address record containing all linked users.
 * @param password - The new plaintext password to hash and set.
 * @param hasTemporaryPassword - Whether this is a temporary password (only for the source user).
 */
export async function setPasswordOfTheSharedUsers(
  sharedEmailAddressInfo: SharedEmailAddressInfo, 
  password: string,
  hasTemporaryPassword = true
) {
  const setPasswordOfSharedEmailUsers = functions.httpsCallable("setPasswordOfSharedEmailUsers");

  await setPasswordOfSharedEmailUsers({
    sharedEmailAddressId: sharedEmailAddressInfo.id,
    password: password,
    hasTemporaryPassword: hasTemporaryPassword,
  });
}
